import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  useColorModeValue,
  ButtonGroup,
} from "@chakra-ui/react";
import Card from "components/card/Card";
import InvoicesTable from "./components/InvoicesTable";
import { columnsDataInvoices } from "./variables/columnsData";
import Pagination from "components/pagination";
import { useToastNotification } from 'utils/toast-utils';
import CreateInvoiceModal from "components/modals/CreateInvoiceModal";
import InvoiceDetails from "./components/InvoiceDetails";
import { useDispatch, useSelector } from 'react-redux';
import { fetchAllClients } from 'features/users/actions';
import { createAdminInvoice, fetchAllInvoices, fetchAdminDraftInvoices } from 'features/invoices/actions';

const InvoiceManagement = () => {
  const dispatch = useDispatch();
  const { clients, clientsLoading } = useSelector((state) => state.users);
  const { invoices, loading, pagination, draftInvoices } = useSelector((state) => state.invoices);
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const bgInput = useColorModeValue("transparent", "navy.900");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const { showToast } = useToastNotification();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  const [activeFilters, setActiveFilters] = useState([]);

  // Fetch clients when component mounts
  useEffect(() => {
    dispatch(fetchAllClients());
  }, [dispatch]);

  // Replace the dummy data with real data
  useEffect(() => {
    const fetchData = async () => {
      if (activeTab === "Draft") {
        await dispatch(fetchAdminDraftInvoices({ page: currentPage, limit: 0 }));
      } else {
        await dispatch(fetchAllInvoices({
          page: currentPage,
          limit: 0,
          searchTerm,
          statusFilters: activeFilters
        }));
      }
    };

    const debounced = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(debounced);
  }, [dispatch, currentPage, itemsPerPage, searchTerm, activeTab, activeFilters]);

  // Update the filtered data to use real data instead of dummy data
  const filteredData = useMemo(() => {
    switch (activeTab) {
      case "Draft":
        return draftInvoices || [];
      case "All":
      default:
        return invoices || [];
    }
  }, [invoices, draftInvoices, activeTab]);

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage]);

  const handlePageChange = useCallback((data, page, limit) => {
    setCurrentPage(page);
    setItemsPerPage(limit);
  }, []);

  const handleCreateInvoice = async (invoiceData) => {
    try {
      await dispatch(createAdminInvoice(invoiceData)).unwrap();
      setIsCreateModalOpen(false);
      showToast('Success', 'Invoice created successfully', 'success');
      // Refresh the invoice list
      dispatch(fetchAllInvoices({ page: currentPage, limit: itemsPerPage, searchTerm }));
    } catch (error) {
      showToast('Error', error.message || 'Failed to create invoice', 'error');
    }
  };

  // Update the Create Invoice button click handler
  const handleCreateButtonClick = () => {
    setIsCreateModalOpen(true);
  };

  const handleRowClick = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetails(true);
  };

  const handleEditDraft = (draft) => {
    setEditingDraft(draft);
    setIsCreateModalOpen(true);
  };

  const handleCloseModal = () => {
    setEditingDraft(null);
    setIsCreateModalOpen(false);
  };

  const handleFilterApply = (selectedStatuses) => {
    setActiveFilters(selectedStatuses);
  };

  return (
    <Card borderRadius="0" p="0">
      {showDetails ? (
        <InvoiceDetails
          invoiceData={selectedInvoice}
          onBack={() => {
            setShowDetails(false);
            setSelectedInvoice(null);
          }}
        />
      ) : (
        <Flex direction="column">
          <Flex justify="space-between" align="center" mb={4}>
            <Text color={textColor} fontSize="30px" fontWeight="700">
              Invoice Management
            </Text>
            <Flex gap={4}>
              <Input
                placeholder="Search"
                borderRadius="4px"
                size="md"
                width="218px"
                height="40px"
                bg={bgInput}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {/* <Button
                colorScheme="gray"
                variant="outline"
                height="40px"
              >
                Export Invoice
              </Button>
              <Button
                colorScheme="gray"
                variant="outline"
                height="40px"
              >
                Import Invoice
              </Button> */}
              <Button
                colorScheme="purple"
                height="40px"
                onClick={handleCreateButtonClick}
              >
                Create Invoice
              </Button>
            </Flex>
          </Flex>

          <Card borderRadius="8px" boxShadow="0px 0px 12px 0px #0000001A">
            <InvoicesTable
              columnsData={columnsDataInvoices}
              tableData={currentPageData}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              onRowClick={handleRowClick}
              onEditDraft={handleEditDraft}
              onFilterApply={handleFilterApply}
            />
            <Flex justify="space-between" align="center" mt={4}>
              <Pagination
                data={filteredData}
                onPageChange={handlePageChange}
                initialItemsPerPage={itemsPerPage}
                currentPage={currentPage}
                totalItems={totalItems}
                totalPages={totalPages}
                hasMoreData={currentPage < totalPages}
              />
            </Flex>
          </Card>
        </Flex>
      )}
      
      <CreateInvoiceModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        initialValues={editingDraft}
        isEditingDraft={!!editingDraft}
        isAdmin={true}
        clients={clients}
        isLoading={clientsLoading}
      />
    </Card>
  );
};

export default InvoiceManagement;
